import UserControllerBase from '../base'
import type {
  EOrganizationBalanceType,
  NDeleteOrganization,
  NGetAllDescendentOrganizations,
  NGetEligibleDestinationOrganizations,
} from './types.js'
import { EOrganizationStatus } from './types.js'
import type {
  NGetOrganizations,
  NGetBusinessAreas,
  NGetUsers,
  NDesactivateUser,
  NActivateUser,
  NDeleteUser,
  NChangeRole,
  NGetUser,
  NGetOrganizationUsers,
  NGetSuborganizations,
  NOrganizationBalanceType,
  NCreateOrUpdateOrganization,
  NGetOrganizationType,
} from './types'
import type { TResponseBase } from '~/services/MSAPI/types'

export default class UserControllerOrganization extends UserControllerBase {
  static readonly controllerPath = 'organization'
  private readonly serviceUrl: string

  constructor() {
    super(UserControllerOrganization.controllerPath)
    this.serviceUrl = this.getFullServiceUrl()
  }

  async getOrganization(
    organizationId: string,
  ): TResponseBase<NGetOrganizations.IOrganization> {
    return await useFetchInstance(`${this.serviceUrl}/${organizationId}`, {
      method: 'GET',
    })
  }

  async getOrganizations(
    params?: NGetOrganizations.IParams,
  ): TResponseBase<NGetOrganizations.IResponseData> {
    return await useFetchInstance(`${this.serviceUrl}/organizations`, {
      method: 'GET',
      params,
    })
  }

  async getOrganizationUsers(
    organizationId: string,
    status: EOrganizationStatus = EOrganizationStatus.Active,
  ): TResponseBase<NGetOrganizationUsers.IOrganizationUser[]> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/users`,
      {
        method: 'GET',
        params: { status },
      },
    )
  }

  // TODO: REQUEST FORA DE PADRÃO TRESPONSEBASE (O BACKEND RETORNA FORA DO PADRÃO)
  async getBusinessAreas(
    params?: NGetBusinessAreas.IParams,
  ): Promise<NGetBusinessAreas.IResponseData[]> {
    return await useFetchInstance(`${this.serviceUrl}/items/business-areas`, {
      method: 'GET',
      params,
    })
  }

  async createOrganization(
    data: NCreateOrUpdateOrganization.IRequestBody,
  ): TResponseBase<NGetOrganizations.IOrganization> {
    return await useFetchInstance(`${this.serviceUrl}`, {
      method: 'POST',
      body: data,
    })
  }

  async updateOrganization(
    organizationId: string,
    data: NCreateOrUpdateOrganization.IRequestBody,
  ) {
    return await useFetchInstance(`${this.serviceUrl}/${organizationId}`, {
      method: 'PUT',
      body: data,
    })
  }

  async getUsers(
    organizationId: string,
    params?: NGetUsers.IParams,
  ): TResponseBase<NGetUsers.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/userlist`,
      {
        method: 'GET',
        params,
      },
    )
  }

  async activateUser(
    organizationId: string,
    userId: string,
    params?: NDesactivateUser.IParams,
  ): TResponseBase<NDesactivateUser.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/users/${userId}/enableorganizationuser`,
      {
        method: 'PATCH',
        params,
      },
    )
  }

  async desactivateUser(
    organizationId: string,
    userId: string,
    params?: NActivateUser.IParams,
  ): TResponseBase<NActivateUser.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/users/${userId}/disableorganizationuser`,
      {
        method: 'PATCH',
        params,
      },
    )
  }

  async deleteUser(
    organizationId: string,
    userId: string,
    params?: NDeleteUser.IParams,
  ): TResponseBase<NDeleteUser.TResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/user/${userId}`,
      {
        method: 'DELETE',
        params,
      },
    )
  }

  async changeRole(
    organizationId: string,
    userId: string,
    body: NChangeRole.IBody,
    params?: NChangeRole.IParams,
  ): TResponseBase<NChangeRole.TResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/users/${userId}/changerole`,
      {
        method: 'PATCH',
        body,
        params,
      },
    )
  }

  async getUser(
    organizationId: string,
    userId: string,
    params?: NGetUser.IParams,
  ): TResponseBase<NGetUser.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/users/${userId}`,
      {
        method: 'GET',
        params,
      },
    )
  }

  async getSuborganizations(
    organizationId: string,
    params: NGetSuborganizations.IRequest,
  ): TResponseBase<NGetSuborganizations.IResponse> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/organizations`,
      {
        method: 'GET',
        params,
      },
    )
  }

  async getBalanceConfigType(
    organizationId?: string,
    params?: NOrganizationBalanceType.IRequestParams,
  ): TResponseBase<EOrganizationBalanceType[]> {
    if (organizationId)
      return await useFetchInstance(
        `${this.serviceUrl}/${organizationId}/assignableorganizationbalanceconfigtypesforupdate`,
        {
          method: 'GET',
        },
      )
    else
      return await useFetchInstance(
        `${this.serviceUrl}/assignableorganizationbalanceconfigtypesforcreate`,
        {
          method: 'GET',
          params,
        },
      )
  }

  async getBillingInfo(
    organizationId: string,
  ): TResponseBase<NActivateUser.IGetBillingInfo> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/billinginfo`,
      {
        method: 'GET',
      },
    )
  }

  async getAllDescendentOrganizations(
    ancestorOrganizationId: string,
    params: NGetAllDescendentOrganizations.IParams,
  ): TResponseBase<NGetAllDescendentOrganizations.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${ancestorOrganizationId}/AllDescendents`,
      {
        method: 'GET',
        params,
      },
    )
  }

  async deleteOrganization(
    organizationId: string,
  ): TResponseBase<NDeleteOrganization.TResponseData> {
    return await useFetchInstance(`${this.serviceUrl}/${organizationId}`, {
      method: 'DELETE',
    })
  }

  async getEligibleDestinationOrganizations(
    organizationId: string,
    params: NGetEligibleDestinationOrganizations.IRequestParams,
  ): TResponseBase<NGetEligibleDestinationOrganizations.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/geteligibledestinationorganizations`,
      {
        method: 'GET',
        params,
      },
    )
  }

  async changeParentOrganization(
    organizationId: string,
    data: NGetEligibleDestinationOrganizations.ChangeParentOrganizationRequest,
  ): TResponseBase<void> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/changeparentorganization`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  }

  async getOrganizationType(
    organizationId: string,
  ): TResponseBase<Array<NGetOrganizationType.IResponseData>> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/items/types`,
      {
        method: 'GET',
      },
    )
  }

  async canCreateNewSuborganization(
    organizationId: string,
  ): TResponseBase<boolean> {
    return await useFetchInstance(
      `${this.serviceUrl}/${organizationId}/canCreateNewSuborganization `,
      {
        method: 'GET',
      },
    )
  }
}
